// ═══════════════════════════════════════════════════════════════
// Permissions Store — GAB native permissions
// Fetches resolved permissions from /my-permissions after login.
// Replaces the custom roleStore for all permission checks.
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { getMyPermissions, getUserRoles, listRoles, getRoleTablePermissions, listAppTables } from '../lib/gabAdmin';
import type { TablePermission, GabRole } from '../lib/gabAdmin';
import { MODULES } from '../lib/modules';

// ── Role IDs provisioned 2026-05-09 ──────────────────────────────
export const ROLE_IDS: Record<string, string> = {
  system_admin:          '019e0ee0-eb51-7755-91ed-0eeb96de1066',
  auditor:               '019e0ee0-ed1b-7038-8241-719688b18181',
  observer:              '019e0ee0-ee90-7230-8b82-a4c0603d9e15',
  eoc_director:          '019e0ee0-eff0-79c2-a6ba-4e4a59c47b31',
  incident_commander:    '019e0ee0-f151-7f17-83a2-93f0b5cec35d',
  deputy_ic:             '019e0ee0-f2b1-7f27-a330-b343d1fa867c',
  pio:                   '019e0ee0-f441-77e5-86e6-f2aaecf2c5ca',
  safety_officer:        '019e0ee0-f5ca-7af3-b0ab-e46df7788dc9',
  liaison_officer:       '019e0ee0-f766-75c4-952f-c5dff487922c',
  ops_section_chief:     '019e0ee0-f8ee-7c15-bbbd-59054f50dc0f',
  planning_section_chief:'019e0ee0-fa82-7422-af2e-8344ad1a006f',
  logistics_section_chief:'019e0ee0-fbe2-72ef-ad96-7f0cdda2da26',
  finance_section_chief: '019e0ee0-fd62-7125-8ebd-dc1818d44998',
  emergency_manager:     '019e0ee0-febf-770c-88f7-be41df0c2c4d',
  deputy_em:             '019e0ee1-002b-76aa-a775-dbcb87d3bdd6',
  recovery_coordinator:  '019e0ee1-019c-783e-a2bf-b1ba77a4f6bd',
  mitigation_planner:    '019e0ee1-02f5-783d-8c0c-27b8b83ebe7a',
  debris_manager:        '019e0ee1-0455-72f6-807e-b389627469ab',
  debris_supervisor:     '019e0ee1-05db-7cf0-944d-2ae9a6d151cb',
  debris_monitor:        '019e0ee1-0741-7d9a-9b22-89a99b239a47',
  debris_driver:         '019e0ee1-089a-7fa6-94a3-f7bbca375509',
  debris_clerk:          '019e0ee1-0a04-7725-84dd-eda24d2c5660',
  shelter_manager:       '019e0ee1-0b6d-7175-8434-c5db5f76c2e5',
  shelter_intake:        '019e0ee1-0cd3-771f-813d-035ca49043e5',
  shelter_volunteer:     '019e0ee1-0e53-7fdb-8b4a-eff1d42a0dad',
  shelter_medical:       '019e0ee1-0fc0-767a-9175-337433478968',
  volunteer_coordinator: '019e0ee1-1133-7b20-ac4f-2539185d53f5',
  volunteer:             '019e0ee1-12ba-799f-8126-f1173ff243ad',
  cert_leader:           '019e0ee1-1478-7425-be97-d16db1825715',
  cert_member:           '019e0ee1-1611-7894-a207-637166ed766e',
  pa_manager:            '019e0ee1-177a-75bc-ac0f-09dc524c80f2',
  grants_admin:          '019e0ee1-18df-7419-97ec-771d34851a48',
  damage_assessor:       '019e0ee1-1a43-7500-9dbd-f41e0958e5e4',
  tl_clerk:              '019e0ee1-1ba9-7d9b-aa77-041d0afef0da',
  timelabor_only:        '019e0ee1-1d1a-7f62-aeb0-25a31560d5ff',
};

export interface ModuleAccess {
  canView:  boolean;
  canEdit:  boolean;
  canAdmin: boolean;
}

interface PermissionsState {
  // Resolved table permissions for the current user
  tablePerms: Record<string, TablePermission>;   // key → TablePermission
  // Current user's assigned roles
  myRoles: GabRole[];
  // All available roles (for admin UI + View As picker)
  allRoles: GabRole[];
  loaded: boolean;
  loading: boolean;

  // View As — simulates a role's permissions for demo purposes
  viewAsRoleId:   string | null;
  viewAsRoleName: string | null;
  viewAsPerms:    Record<string, TablePermission>;
  setViewAs:   (roleId: string, roleName: string) => Promise<void>;
  clearViewAs: () => void;

  // Load after login — call with the user's GAB userId
  load: (userId: string) => Promise<void>;
  loadAllRoles: () => Promise<void>;
  clear: () => void;

  // Permission helpers
  canView:   (tableKey: string) => boolean;
  canAdd:    (tableKey: string) => boolean;
  canEdit:   (tableKey: string) => boolean;
  canDelete: (tableKey: string) => boolean;
  isAdmin:   () => boolean;
  getModuleAccess: (moduleId: string) => ModuleAccess;
  hasRole: (slug: string) => boolean;
}

export const usePermissionsStore = create<PermissionsState>((set, get) => ({
  tablePerms:     {},
  myRoles:        [],
  allRoles:       [],
  loaded:         false,
  loading:        false,
  viewAsRoleId:   null,
  viewAsRoleName: null,
  viewAsPerms:    {},

  load: async (userId: string) => {
    set({ loading: true });
    try {
      const [permsRes, rolesRes] = await Promise.all([
        getMyPermissions().catch(() => ({ tables: [] })),
        getUserRoles(userId).catch(() => []),
      ]);
      const tablePerms: Record<string, TablePermission> = {};
      for (const t of permsRes.tables) {
        tablePerms[t.key] = t;
      }
      set({ tablePerms, myRoles: rolesRes, loaded: true });
    } finally {
      set({ loading: false });
    }
  },

  setViewAs: async (roleId: string, roleName: string) => {
    // Fetch role perms and app tables in parallel so we can map tableId UUID → base36 key.
    // The role-specific endpoint returns tableId (UUID) not key, so we need the cross-ref.
    const [perms, appTables] = await Promise.all([
      getRoleTablePermissions(roleId),
      listAppTables(),
    ]);
    const uuidToKey: Record<string, string> = {};
    for (const t of appTables) uuidToKey[t.id] = t.key;

    const viewAsPerms: Record<string, TablePermission> = {};
    for (const p of perms) {
      const uuid = p.tableId ?? p.id;
      const key  = p.key ?? uuidToKey[uuid];
      if (key) viewAsPerms[key] = p;
    }
    set({ viewAsRoleId: roleId, viewAsRoleName: roleName, viewAsPerms });
  },

  clearViewAs: () => set({ viewAsRoleId: null, viewAsRoleName: null, viewAsPerms: {} }),

  loadAllRoles: async () => {
    const roles = await listRoles().catch(() => []);
    set({ allRoles: roles });
  },

  clear: () => set({ tablePerms: {}, myRoles: [], loaded: false, viewAsRoleId: null, viewAsRoleName: null, viewAsPerms: {} }),

  canView: (key) => {
    const { viewAsRoleId, viewAsPerms, tablePerms, loaded } = get();
    if (viewAsRoleId) return viewAsPerms[key]?.canView ?? false;
    return tablePerms[key]?.canView ?? !loaded;
  },
  canAdd: (key) => {
    const { viewAsRoleId, viewAsPerms, tablePerms, loaded } = get();
    if (viewAsRoleId) return viewAsPerms[key]?.canAdd ?? false;
    return tablePerms[key]?.canAdd ?? !loaded;
  },
  canEdit: (key) => {
    const { viewAsRoleId, viewAsPerms, tablePerms, loaded } = get();
    if (viewAsRoleId) return viewAsPerms[key]?.canEdit ?? false;
    return tablePerms[key]?.canEdit ?? !loaded;
  },
  canDelete: (key) => {
    const { viewAsRoleId, viewAsPerms, tablePerms, loaded } = get();
    if (viewAsRoleId) return viewAsPerms[key]?.canDelete ?? false;
    return tablePerms[key]?.canDelete ?? !loaded;
  },

  isAdmin: () => {
    const { viewAsRoleId, myRoles } = get();
    if (viewAsRoleId) {
      // Simulate admin only for top-level admin roles
      return [ROLE_IDS.system_admin, ROLE_IDS.eoc_director, ROLE_IDS.emergency_manager, ROLE_IDS.incident_commander].includes(viewAsRoleId);
    }
    return myRoles.some(r => {
      const name = (r.name ?? '').toLowerCase();
      return name.includes('system administrator') || name.includes('eoc director');
    });
  },

  getModuleAccess: (moduleId: string): ModuleAccess => {
    const { viewAsRoleId, viewAsPerms, tablePerms, loaded } = get();

    // Before own permissions load (and not in view-as), allow everything
    if (!viewAsRoleId && !loaded) return { canView: true, canEdit: true, canAdmin: true };

    const active = viewAsRoleId ? viewAsPerms : tablePerms;

    const mod = MODULES.find(m => m.id === moduleId);
    if (!mod) return { canView: false, canEdit: false, canAdmin: false };

    const primaryKey = (mod.tables[0] as string | { key?: string });
    const key = typeof primaryKey === 'string' ? primaryKey : primaryKey?.key ?? '';
    const perm = active[key];

    // In view-as mode, no entry = no access. Own perms: no entry = no restriction yet.
    if (!perm) return viewAsRoleId
      ? { canView: false, canEdit: false, canAdmin: false }
      : { canView: true, canEdit: true, canAdmin: true };

    return {
      canView:  perm.canView,
      canEdit:  perm.canEdit,
      canAdmin: perm.canAdd && perm.canEdit && perm.canDelete,
    };
  },

  hasRole: (slug: string) => {
    const { viewAsRoleId, myRoles } = get();
    const id = ROLE_IDS[slug];
    if (viewAsRoleId) return viewAsRoleId === id;
    return myRoles.some(r => r.id === id || r.name === slug);
  },
}));
